/**
 * Lift2Fit — roster pieces shared by the coach dashboard.
 *
 * A coach still trains: their own week sits above the roster, because the
 * coach role is additive rather than a mode switch. Coaches see the same
 * roster across every client, plus programme authoring.
 */
import { useState } from 'react';
import type { RosterDashboard, Trainee } from '@/lib/api';
import { api } from '@/lib/api';
import { formatDate, pluralize, timeAgo } from '@/lib/format';
import { useUnitPreference } from '@/lib/useUnitPreference';
import { useSession } from '@/lib/useSession';
import { Button } from '@/components/ui';
import { AlertIcon, ArrowRightIcon, CheckIcon, FlameIcon, GridIcon, TrendUpIcon } from '@/lib/icons';
import {
  AdherenceBar,
  EmptyState,
  FlagChips,
  LiveMessage,
  MetricTile,
  Panel,
  PersonaBadge,
  StatusChip,
  TrackBadge,
  WeekStrip,
} from './components';
import { trackMeta } from './demoData';
import { Link } from './router';

/* ---------------------------------------------------------------------------
   A coach's own training
   ------------------------------------------------------------------------ */

export function OwnWeekPanel({ week, streak, sessionWord }: { week: RosterDashboard['ownWeek']; streak: RosterDashboard['ownStreak']; sessionWord: string }) {
  if (!week) return null;
  return (
    <Panel
      title="Your own training"
      subtitle={`Coach first, lifter always — ${week.completed} of ${pluralize(week.target, sessionWord)} this week`}
      actions={
        streak ? (
          <StatusChip tone="accent" icon={<FlameIcon />}>
            {pluralize(streak.currentWeeks, 'week')} streak
          </StatusChip>
        ) : null
      }
    >
      <WeekStrip week={week} />
      <Link
        to="/dashboard"
        className="inline-flex w-fit items-center gap-2 border border-hairline px-4 py-2 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
      >
        Open my dashboard
        <ArrowRightIcon />
      </Link>
    </Panel>
  );
}

/* ---------------------------------------------------------------------------
   Counts and averages
   ------------------------------------------------------------------------ */

export function RosterSummary({ dashboard }: { dashboard: RosterDashboard }) {
  const { unit } = useUnitPreference();
  const { counts, averages } = dashboard;
  const isConditioning = dashboard.track === 'conditioning';
  const isWellness = dashboard.track === 'wellness';

  return (
    <section aria-label="Roster summary" className="grid grid-cols-2 gap-3 lg:grid-cols-5">
      <MetricTile label="Clients" value={String(counts.trainees)} hint={`${counts.active} active · ${counts.invited} invited`} />
      <MetricTile
        label="Flagged"
        value={String(counts.flagged)}
        hint={counts.flagged ? 'need a look this week' : 'nothing flagged'}
        hintTone={counts.flagged ? 'danger' : 'success'}
        icon={counts.flagged ? <AlertIcon /> : <CheckIcon />}
      />
      <MetricTile
        label="Avg adherence"
        value={averages.compliancePct === null ? '—' : `${averages.compliancePct}%`}
        hint="logged vs prescribed, last 7 days"
        hintTone={averages.compliancePct !== null && averages.compliancePct < 80 ? 'warning' : 'success'}
      />
      <MetricTile
        label={isWellness ? 'Mobility / client' : 'Sessions / client'}
        value={String(averages.sessionsPerTrainee7)}
        hint={isWellness ? 'mobility sessions, last 7 days' : 'last 7 days'}
        icon={<TrendUpIcon />}
      />
      {!isWellness ? (
        <MetricTile
          label={isConditioning ? 'Interval minutes' : 'Roster volume'}
          value={
            isConditioning
              ? `${dashboard.trackMetrics.intervalMinutes ?? 0} min`
              : `${Math.round(unit === 'imperial' ? averages.volumeKg7 * 2.20462 : averages.volumeKg7).toLocaleString('en-GB')} ${unit === 'imperial' ? 'lb' : 'kg'}`
          }
          hint="your own training, last 7 days"
        />
      ) : (
        <MetricTile
          label="Pain score"
          value={`${dashboard.trackMetrics.painScore ?? '—'} / 10`}
          hint={dashboard.trackMetrics.feel ?? 'self-reported'}
          tone={dashboard.trackMetrics.painScore !== null && dashboard.trackMetrics.painScore >= 4 ? 'danger' : 'default'}
        />
      )}
    </section>
  );
}

/* ---------------------------------------------------------------------------
   Needs attention
   ------------------------------------------------------------------------ */

export function NeedsAttention({ attention }: { attention: RosterDashboard['attention'] }) {
  return (
    <Panel
      title="Needs attention"
      subtitle={attention.length ? 'Sorted worst first — one line each, no table.' : 'Everyone is on track.'}
    >
      {attention.length ? (
        <ul className="flex flex-col gap-3">
          {attention.map((item) => (
            <li key={item.userId} className="flex items-start gap-3 border-l-2 border-l-danger bg-page-subtle px-3 py-2">
              <AlertIcon className="mt-0.5 shrink-0 text-danger" />
              <span className="flex min-w-0 flex-col">
                <span className="text-[14px] font-bold text-ink">{item.displayName}</span>
                <span className="text-[13px] text-ink-muted">{item.reason}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          title="Nothing needs attention"
          body="No missed weeks, no pain reports and no adherence drops in this roster. Keep the plan steady."
          icon={<CheckIcon className="h-8 w-8 text-success" />}
        />
      )}
    </Panel>
  );
}

/* ---------------------------------------------------------------------------
   The roster list
   ------------------------------------------------------------------------ */

export function TraineeList({
  trainees,
  showTrainer = false,
  title = 'Roster',
}: {
  trainees: Trainee[];
  showTrainer?: boolean;
  title?: string;
}) {
  const [query, setQuery] = useState('');
  const filtered = query
    ? trainees.filter(
        (trainee) =>
          trainee.displayName.toLowerCase().includes(query.toLowerCase()) ||
          trainee.email.toLowerCase().includes(query.toLowerCase()),
      )
    : trainees;

  return (
    <Panel
      title={title}
      subtitle="Flagged first, then least recently seen."
      actions={
        <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
          <span className="sr-only">Filter clients</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Filter"
            className="h-11 w-36 border border-hairline bg-page px-2 text-[12px] text-ink"
          />
        </label>
      }
      bodyClassName="gap-3"
    >
      {filtered.length ? (
        <ul className="flex flex-col gap-3">
          {filtered.map((trainee) => {
            const meta = trackMeta(trainee.track);
            return (
              <li key={trainee.userId} className="l2f-row-link flex flex-col gap-3 border border-hairline bg-surface p-4 transition-colors duration-150">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-col gap-1">
                    <p className="flex items-center gap-2 text-[15px] font-bold text-ink">
                      {trainee.displayName}
                      {trainee.status === 'invited' ? <StatusChip tone="info">Invited</StatusChip> : null}
                    </p>
                    <p className="text-[13px] text-ink-muted">
                      {trainee.focus ?? `${meta.label} training`}
                      {showTrainer && trainee.trainerName ? ` · coach ${trainee.trainerName}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <TrackBadge track={trainee.track} />
                    <FlagChips flags={trainee.flags} />
                  </div>
                </div>

                <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="flex flex-col gap-1">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Adherence</dt>
                    <dd>
                      <AdherenceBar pct={trainee.compliancePct} />
                    </dd>
                  </div>
                  <div className="flex flex-col gap-1">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Last session</dt>
                    <dd className="text-[13px] text-ink">
                      {trainee.lastSessionAt ? timeAgo(trainee.lastSessionAt) : 'Never logged'}
                    </dd>
                  </div>
                  <div className="flex flex-col gap-1">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Streak</dt>
                    <dd className="l2f-tabular font-mono text-[13px] font-bold text-ink">
                      {trainee.currentStreakWeeks} wk
                    </dd>
                  </div>
                  <div className="flex flex-col gap-1">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Sessions / 7d</dt>
                    <dd className="l2f-tabular font-mono text-[13px] font-bold text-ink">{trainee.sessionsLast7}</dd>
                  </div>
                </dl>

                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-hairline pt-3">
                  <PersonaBadge track={trainee.track} persona={trainee.coachPersona} />
                  <span className="text-[12px] text-ink-muted">
                    Linked {trainee.linkedAt ? formatDate(trainee.linkedAt) : '—'} · shares{' '}
                    {trainee.sharedFields.join(', ') || 'nothing'}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState
          title={query ? 'No client matches that filter' : 'No clients yet'}
          body={
            query
              ? 'Try another name or email address.'
              : `Invite a client by email and their log appears here — ${metaLabelForEmpty()} work included.`
          }
          icon={<GridIcon className="h-8 w-8 text-accent" />}
        />
      )}
    </Panel>
  );
}

function metaLabelForEmpty(): string {
  return 'strength, bodybuilding, conditioning, performance, wellness and general';
}

/* ---------------------------------------------------------------------------
   Invite by email
   ------------------------------------------------------------------------ */

export function InviteForm({ onInvited }: { onInvited?: (trainee: Trainee) => void }) {
  const session = useSession();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'success' | 'danger'>('info');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.includes('@')) {
      setTone('danger');
      setMessage('Enter a valid email address.');
      return;
    }
    setBusy(true);
    setTone('info');
    setMessage('Sending the invite…');
    const result = await api.inviteTrainee(email);
    setBusy(false);
    setTone('success');
    setMessage(
      result.source === 'demo'
        ? `Demo mode: invite for ${email} recorded locally. Start the API to send it for real.`
        : `Invite sent to ${email}.`,
    );
    onInvited?.(result.data);
    setEmail('');
  };

  return (
    <Panel
      title="Invite a client"
      subtitle="They keep their own account; you get the fields they choose to share."
      as="section"
    >
      <form className="flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={submit}>
        <label className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Email</span>
          <input
            type="email"
            name="inviteEmail"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="client@example.com"
            className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
          />
        </label>
        <Button type="submit" disabled={busy}>
          {busy ? 'Sending…' : 'Send invite'}
        </Button>
      </form>
      <LiveMessage message={message} tone={tone} />
      {session.isDemo ? (
        <p className="text-[12px] text-ink-muted">
          Demo data — start the API to use your own roster. Nothing leaves this device.
        </p>
      ) : null}
    </Panel>
  );
}
